import { Typography } from 'src/app/components/Elements/Typography';
import { ContrastedTitle } from 'src/app/components/Pages/Order/Tracking/Elements';
import { useDeliveryAddressById } from 'src/app/hooks/data/deliveryAddress';
import type { DeliveryAddress, FacilityHour } from 'src/app/types';
import { DeliveryMethodType } from 'src/consts';

import { ADDRESS_UNAVAILABLE } from '../../consts';

import { AddressSection } from './AddressSection';
import { HoursSection } from './HoursSection';
import { CardItemContainer, ExpressScriptsNote } from './styles';

interface CardDeliveryDetailsItemProps {
  deliveryAddress?: DeliveryAddress;
  deliveryMethodKey: DeliveryMethodType;
  carrierName?: string;
  hours?: FacilityHour[];
  closingTimeFlavorText: string;
}

export const CardDeliveryDetailsItem = ({
  deliveryAddress,
  deliveryMethodKey,
  carrierName = '',
  hours,
  closingTimeFlavorText,
}: CardDeliveryDetailsItemProps) => {
  const deliveryAddressId = deliveryAddress?.id ?? '';
  const { isLoading: isLoadingDeliveryAddress } = useDeliveryAddressById(deliveryAddressId);

  const { name, address, addressTwo, instructions, city, state, zipCode } = deliveryAddress || {};
  // If there is no delivery address (6 percent of orders as of Jan 2022), just show the address which is 'Address Unavailable'.
  // Without below the text would render like 'Address Unavailable, ,'
  const combinedAddress = address === ADDRESS_UNAVAILABLE ? address : `${address}, ${city}, ${state} ${zipCode}`;

  const isPickupStationOrder = deliveryMethodKey === DeliveryMethodType.pickupStation;
  const isESIMailOrder = deliveryMethodKey === DeliveryMethodType.mail;

  return (
    <>
      <AddressSection
        deliveryAddressId={deliveryAddressId}
        name={name}
        address={address}
        addressTwo={addressTwo}
        city={city}
        state={state}
        zipCode={zipCode}
        combinedAddress={combinedAddress}
        isPickupStationOrder={isPickupStationOrder}
      />
      {isPickupStationOrder && hours && hours.length > 0 && (
        <HoursSection hours={hours} closingTimeFlavorText={closingTimeFlavorText} />
      )}
      {isESIMailOrder ? (
        <ExpressScriptsNote variant="h2">
          {!carrierName || carrierName === '' ? `Delivery` : `${carrierName} delivery`} by Express Scripts®
        </ExpressScriptsNote>
      ) : (
        <CardItemContainer>
          <ContrastedTitle
            title={isPickupStationOrder ? 'Pickup Instructions' : 'Instructions'}
            inOrderSummary={true}
          />
          {isLoadingDeliveryAddress ? (
            <Typography variant="body" aria-hidden="true">
              ...
            </Typography>
          ) : (
            <Typography data-cy={`order-address-two`} variant="body">
              {instructions?.trim() ? instructions : 'None provided.'}
            </Typography>
          )}
        </CardItemContainer>
      )}
    </>
  );
};
